import { SerializedConstructor } from '@langchain/core/load/serializable';
import { ListItemDecorator, Option, Select } from '@mui/joy';

import { useEffect, useRef } from 'react';

import { PromptTool } from '@/utils/ai-tools';

import { ManifestEditorProps } from '../../Manifest.utils';
import { getToolDisplayName, getToolValue } from '../utils/toolsUtils';
import {
  DEFAULT_TOOL_CHOICE,
  EToolChoice,
  TOOL_CHOICE_LABELS,
} from './constants';

const GenericToolChoiceSelect = (
  props: ManifestEditorProps<SerializedConstructor> & {
    tools: PromptTool[];
    toolChoices: EToolChoice[];
    toolPrefix: 'tool' | 'func';
    disableSpecificToolChoice?: boolean;
    provider: string;
  }
) => {
  const handleCallChange = (value: string | null) => {
    if (!value) return;

    props.onOptionsChange({
      ...props.options,
      function_call: undefined,
      tool_choice: value,
    });
  };

  const handleCallChangeRef = useRef(handleCallChange);
  handleCallChangeRef.current = handleCallChange;

  const { tool_choice: toolChoice } = props.options;

  useEffect(() => {
    if (props.tools.length > 0) {
      if (!toolChoice) {
        handleCallChangeRef.current?.(DEFAULT_TOOL_CHOICE);
      }
    }
  }, [
    toolChoice,
    props.toolChoices,
    props.tools.length,
    props.toolPrefix,
    props.disableSpecificToolChoice,
  ]);

  return (
    <Select
      value={toolChoice}
      renderValue={(value) => {
        if (props.tools.length === 0) {
          return 'No tools available. Define a tool to enable';
        }
        if (
          value?.value &&
          Object.values(EToolChoice).includes(value.value as EToolChoice)
        ) {
          return TOOL_CHOICE_LABELS[value?.value];
        }
        return value?.label;
      }}
      defaultValue={DEFAULT_TOOL_CHOICE}
      onChange={(_, value) => handleCallChange(value)}
      size="sm"
      disabled={props.readOnly || props.tools.length === 0}
      endDecorator={
        typeof toolChoice === 'string' &&
        Object.values(EToolChoice).includes(toolChoice as EToolChoice) && (
          <ListItemDecorator>
            <pre className="ml-2 rounded-md bg-background px-1">
              {toolChoice}
            </pre>
          </ListItemDecorator>
        )
      }
    >
      {props.toolChoices.map((choice, index) => (
        <Option key={index} value={choice}>
          {TOOL_CHOICE_LABELS[choice]}
          <ListItemDecorator>
            <pre className="ml-2 rounded-md bg-background px-1">{choice}</pre>
          </ListItemDecorator>
        </Option>
      ))}
      {props.tools.map((tool, index) => (
        <Option key={index} value={getToolValue(tool, `tool-${index}`)}>
          <span className="font-mono">
            {getToolDisplayName(tool, props.provider)}
          </span>
        </Option>
      ))}
    </Select>
  );
};

export default GenericToolChoiceSelect;
